import type { DateRange, SeatStatus } from "@/types/api";

/**
 * Every cache key in one place, so invalidation is never a guess.
 * Keys are hierarchical: invalidating ["members"] clears every filtered list.
 */
export const qk = {
	org: ["org"] as const,
	members: (f?: { seatStatus?: SeatStatus }) =>
		f?.seatStatus
			? (["members", f.seatStatus] as const)
			: (["members"] as const),
	usage: (r?: DateRange) => ["usage", r?.from ?? "", r?.to ?? ""] as const,
	// Hierarchical like `members`: invalidating ["teams"] clears every variant,
	// so a mutation never has to know which list a screen happens to be showing.
	teams: (f?: { includeMembers?: boolean; includeArchived?: boolean }) =>
		f?.includeMembers || f?.includeArchived
			? ([
					"teams",
					f.includeMembers ? "members" : "",
					f.includeArchived ? "archived" : "",
				] as const)
			: (["teams"] as const),
	team: (id: string) => ["teams", "one", id] as const,
	domains: ["domains"] as const,
	audit: ["audit"] as const,
	disclosure: ["disclosure"] as const,
	invoices: ["invoices"] as const,
	paymentMethod: ["paymentMethod"] as const,
	content: ["content"] as const,
	slack: ["slack"] as const,
	// Under ["jira"], so a disconnect's invalidation also clears the project list.
	jira: ["jira"] as const,
	jiraProjects: ["jira", "projects"] as const,
	jiraWorkHealth: ["jira", "work-health"] as const,
	session: ["session"] as const,
	// Hierarchical: invalidating ["progress"] clears the org view and every
	// member drill-down under it.
	progress: ["progress"] as const,
	memberProgress: (memberId: string) => ["progress", "member", memberId] as const,
	domainDetail: (memberId: string, domain: string) => ["progress", "member", memberId, domain] as const,
	seatQuote: (n: number) => ["seatQuote", n] as const,
	setupQuote: (n: number, code: string) => ["setupQuote", n, code] as const,
};
